/**
 * 会员卡购买
 */
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Alert, Button, Descriptions, Form, message, Select } from 'antd';
import { connect } from 'react-redux';
import cls from 'classnames';
import { debounce, get } from 'lodash';
import { LabeledValue } from 'antd/lib/select';
import intl from 'react-intl-universal';
import { CardInfo } from '@/pages/order/detail/component/Guest/CardInfoModal';
import { buyMemberCard, querySalesperson, updateIdValidatedRes } from '@/redux/actions/guest';
import { CardItem, GuestInitialStateProps, IguestInfo } from '@/redux/reducers/guest';
import { IuserState } from '@/redux/reducers/user';
import {
  fetchCustomerInfo,
  fetchCustomerInfoByWyuid,
  fetchUserAvailbleCardList,
} from '@/api/guest';
import { CertIDType, ContractType } from '@/config';
import Card from '@/utils/card';
import { filterFormParams, renderIntlLabel } from '@/utils';
import { isNil, parseFen2Yuan } from '@/utils/number';
import { transformCardInfo } from '@/utils/transform';
import silverCardUrl from '@/assets/img/silver_card.png';
import silverCardSelectedUrl from '@/assets/img/silver_card_selected.png';
import oldGoldCardUrl from '@/assets/img/old_gold_card.png';
import oldGoldCardSelectedUrl from '@/assets/img/old_gold_card_selected.png';
import oldPlatinumCardUrl from '@/assets/img/old_platinum_card.png';
import oldPlatinumCardSelectedUrl from '@/assets/img/old_platinum_card_selected.png';
import oldDiamondCardUrl from '@/assets/img/old_diamond_card.png';
import oldDiamondCardSelectedUrl from '@/assets/img/old_diamond_card_selected.png';
import goldCardUrl from '@/assets/img/gold_card.png';
import goldCardSelectedUrl from '@/assets/img/gold_card_selected.png';
import platinumCardUrl from '@/assets/img/platinum_card.png';
import platinumCardSelectedUrl from '@/assets/img/platinum_card_selected.png';
import diamondCardUrl from '@/assets/img/diamond_card.png';
import diamondCardSelectedUrl from '@/assets/img/diamond_card_selected.png';
import { trackIDCardReader, IDCardReaderScene } from '@/utils/monitor';
import PhoneCheckModal, { PhoneCheckModalRef } from '@/components/PhoneCheckModal';

import CustomDrawer, { IdrawerRef } from '../CustomDrawer';
import CustomForm, { IFieldItem, IMyFormRef } from '../CustomForm';
import InputMobile from '../InputMobile';
import Payment from '../Payment';
import PayModal from '../Payment/payModal';
import { PayType, PayModalRef, PayWay } from '../Payment/interface';
import BaseMobileInput from '../BaseMobileInput';
import BaseEmailInput, { EMAIL_REG } from '../BaseEmailInput';
import { parseAreaMobileStr } from '../BaseMobileInput/helper';
import { TenantGlobalConfig } from '../Intl/IntlContext/helper';
import styles from './index.module.scss';

type CertInfo = Partial<
  {
    isNew: boolean;
  } & Pick<IguestInfo, 'name' | 'certType' | 'certNo' | 'mobile' | 'email' | 'wyuid'>
>;

export type PurchaseResult = { isBuySuccess: boolean } & CertInfo;

export interface IcardPurchaseRef extends Omit<IdrawerRef, 'openDrawer'> {
  // 会员信息
  openDrawer: (value: CertInfo) => void;
}

const cardCoverImgMap = {
  // 银卡
  A00: {
    source: 'MEMBER_CARD_99',
    url: silverCardUrl,
    selectedUrl: silverCardSelectedUrl,
    bgColorStyle: styles.silver,
    copywriting: intl.get('card_purchase_silver_card'),
  },
  K0000: {
    source: 'MEMBER_CARD_99',
    url: silverCardUrl,
    selectedUrl: silverCardSelectedUrl,
    bgColorStyle: styles.silver,
    copywriting: intl.get('card_purchase_silver_card'),
  },
  // *198会员卡
  K0001: { source: 'MEMBER_CARD_198', url: '', selectedUrl: '' },
  // 黄金卡
  K0002: {
    source: 'MEMBER_CARD_298',
    url: oldGoldCardUrl,
    selectedUrl: oldGoldCardSelectedUrl,
    bgColorStyle: styles.gold,
    copywriting: intl.get('card_purchase_gold_card'),
  },
  // 铂金卡
  K0003: {
    source: 'PLATINUM_CARD',
    url: oldPlatinumCardUrl,
    selectedUrl: oldPlatinumCardSelectedUrl,
    bgColorStyle: styles.platinum,
    copywriting: intl.get('card_purchase_platinum_card'),
  },
  // 钻石卡
  K0004: {
    source: 'DIAMOND_CARD',
    url: oldDiamondCardUrl,
    selectedUrl: oldDiamondCardSelectedUrl,
    bgColorStyle: styles.diamond,
    copywriting: intl.get('card_purchase_diamond_card'),
  },
  // 新卡(黄金卡)
  K0005: {
    source: 'NEW_GOLD_CARD',
    url: goldCardUrl,
    selectedUrl: goldCardSelectedUrl,
    bgColorStyle: styles.gold,
    copywriting: intl.get('card_purchase_gold_card'),
  },
  // 新卡(铂金卡)
  K0006: {
    source: 'NEW_PLATINUM_CARD',
    url: platinumCardUrl,
    selectedUrl: platinumCardSelectedUrl,
    bgColorStyle: styles.platinum,
    copywriting: intl.get('card_purchase_platinum_card'),
  },
  // 新卡(钻石卡)
  K0007: {
    source: 'NEW_DIAMOND_CARD',
    url: diamondCardUrl,
    selectedUrl: diamondCardSelectedUrl,
    bgColorStyle: styles.diamond,
    copywriting: intl.get('card_purchase_diamond_card'),
  },
};

interface CardPurchaseState extends Partial<GuestInitialStateProps> {
  querySalespersonFn?: () => void;
  buyMemberCardFn?: (value: any) => Promise<any>;
  updateIdValidatedResFn?: (..._) => void;
}

interface IcardPurchaseProps extends CardPurchaseState {
  myRef?: any;
  onCloseDrawer?: (value: PurchaseResult) => void;
}

let cardInstance = null;

const CardPurchase: React.FC<IcardPurchaseProps & IuserState> = ({
  myRef,
  onCloseDrawer,
  userInfo,
  salespersonList,
  idValidateInfo,
  querySalespersonFn,
  buyMemberCardFn,
  updateIdValidatedResFn,
  buyCardLoading,
}) => {
  const [cardInfo, setCardInfo] = useState<Partial<CardInfo>>();
  const [showCertLoading, setShowCertLoading] = useState<boolean>(false);
  const [isReadCertSuccess, setIsReadCertSuccess] = useState<boolean>(false);
  // 是否是新办会员卡，需输入身份证
  const [memberInfo, setMemberInfo] = useState<Partial<CertInfo>>({});
  // 会员卡
  const [memberCardList, setMemberCardList] = useState<Array<CardItem>>([]);
  const [selectedCardCode, setSelectedCardCode] = useState<any>();
  // 售卖人
  const [selectedSalesperson, setSelectedSalesperson] = useState<LabeledValue>({
    label: '',
    value: '',
  });
  // 收银台
  const [selectedPayType, setSelectedPayType] = useState<PayType>(3);
  // 会员手机号重复校验
  const phoneCheckModalRef = useRef<PhoneCheckModalRef>();

  const drawerRef = useRef<IdrawerRef>();
  const formRef = useRef<IMyFormRef>();
  const detailRef = useRef<IMyFormRef>();
  const payModalRef = useRef<PayModalRef>();

  const detailAttr: Array<IFieldItem> = useMemo(() => {
    return [
      {
        label: intl.get('bill_refound_res_mobile_no'),
        key: 'mobile',
        type: 'group',
        rules: [
          {
            validator(_, val) {
              const { areaCode, mobileStr } = parseAreaMobileStr(val);
              // 只填一个
              if ((areaCode && !mobileStr) || (!areaCode && mobileStr)) {
                return Promise.reject(
                  new Error(renderIntlLabel('reservation_error_mobile_invalid')),
                );
              }
              // 是否修改过，修改过校验是否脱敏
              if (val && val !== memberInfo?.mobile && val.includes('*')) {
                return Promise.reject(
                  new Error(
                    renderIntlLabel('order_frontDesk_validate_please_check_mobile_error_format'),
                  ),
                );
              }
              // 关键人常用联系方式手机号是否必填
              if (
                TenantGlobalConfig.getSystemConfig().contractType === ContractType.MOBILE &&
                !val
              ) {
                return Promise.reject(
                  new Error(renderIntlLabel('reservation_error_mobile_invalid')),
                );
              }

              return Promise.resolve();
            },
          },
        ],
        component: <BaseMobileInput />,
      },
      {
        label: intl.get('help_store_address_type_email'),
        key: 'email',
        type: 'group',
        rules: [
          {
            validator(_, val) {
              // 修改过，修改过校验是否脱敏
              if (val && val !== memberInfo?.email && !EMAIL_REG.test(val)) {
                return Promise.reject(
                  new Error(
                    renderIntlLabel('order_frontDesk_validate_please_check_email_error_format'),
                  ),
                );
              }
              if (
                TenantGlobalConfig.getSystemConfig().contractType === ContractType.EMAIL &&
                !val
              ) {
                return Promise.reject(
                  new Error(
                    renderIntlLabel('order_frontDesk_validate_please_check_email_error_format'),
                  ),
                );
              }
              return Promise.resolve();
            },
          },
        ],
        component: <BaseEmailInput />,
      },
    ];
  }, [memberInfo]);

  const memberCardPrice = useMemo(() => {
    return memberCardList?.find((item) => item.cardCode === selectedCardCode)?.price;
  }, [selectedCardCode, memberCardList]);

  const getMobileByCertNo = async (params) => {
    // console.log('getMobileByCertNo', params);
    const { name, certType, certNo, wyuid } = params;
    if ((params.isNew && (!name || !certType || !certNo)) || (!params.isNew && !wyuid)) {
      setMemberInfo(params);
      return;
    }
    // 根据证件号查询手机号、备注
    try {
      const result = { ...params };
      let res;
      if (params.isNew) {
        res = await fetchCustomerInfo({ name, certType, certNo });
      } else {
        res = await fetchCustomerInfoByWyuid({ wyuid });
      }
      const { wyuid: currentWyuid, certType: type, mobile, remark, email } = res?.data || {};
      result.wyuid = currentWyuid;
      if (type) {
        result.certType = type;
      }
      if (mobile) {
        result.mobile = mobile;
      }
      if (remark) {
        result.remark = remark;
      }
      if (email) {
        result.email = email;
      }
      setMemberInfo(result);
      updateIdValidatedResFn({ idValidateInfo: {} });

      // 查询用户可购卡列表
      const resonse = await fetchUserAvailbleCardList({ wyuIds: [currentWyuid] });
      if (resonse?.code === 0) {
        const allMap = resonse?.data || {};
        // console.log(resonse?.data, '1----------5');
        setMemberCardList(allMap[currentWyuid] || []);
      }
    } catch (error) {
      setMemberInfo(params);
      updateIdValidatedResFn({ idValidateInfo: error });

      setMemberCardList([]);
      setSelectedCardCode(undefined);
    }
  };

  const closeSocket = () => {
    if (cardInstance && cardInstance.socket) {
      cardInstance.socket.close();
      cardInstance = null;
    }
  };

  const fnReadIdCard = (detail) => {
    if (cardInstance) {
      cardInstance
        .readIdCard(false)
        .then(async (res) => {
          const { name, id } = res;
          // 赋值证件号
          formRef.current?.setFieldsValue({ name, certType: CertIDType.ID_CARD, certNo: id });
          // 根据证件号查询手机号
          getMobileByCertNo({ ...detail, name, certType: CertIDType.ID_CARD, certNo: id });

          setCardInfo(res);
          setShowCertLoading(false);
          setIsReadCertSuccess(true);
          closeSocket();
          trackIDCardReader({ type: IDCardReaderScene.MemberCardPurchase });
        })
        .catch(() => {
          fnReadIdCard(detail);
        });
    }
  };

  const emptyIdCardState = () => {
    setCardInfo({});
    setShowCertLoading(false);
    setIsReadCertSuccess(false);
    closeSocket();
  };

  const emptyMemberState = () => {
    setMemberInfo({});
    setMemberCardList([]);
    setSelectedCardCode(undefined);
    setSelectedSalesperson({ label: '', value: '' });
  };

  const handleOpenDrawer = (value: CertInfo = {}) => {
    drawerRef.current.openDrawer();
    formRef.current?.resetFields();
    detailRef.current?.resetFields();
    updateIdValidatedResFn({ idValidateInfo: {} });
    getMobileByCertNo(value);

    // 读身份证
    if (value?.isNew) {
      setShowCertLoading(true);
      cardInstance = new Card();
      cardInstance.socket.addEventListener('open', () => {
        fnReadIdCard(value);
      });
      cardInstance.socket.addEventListener('error', () => {
        setCardInfo({});
        closeSocket();
      });
    }
    // 查询销售人员
    querySalespersonFn();
  };

  // 下单
  const openPayModal = (data, confirm) => {
    if (!memberCardPrice) {
      message.error(intl.get('cpm_card_purchase_receivable_cannot_empty_select_vip_card'));
      return;
    }
    // 付款单需confirm后下单
    if (!confirm && selectedPayType === PayWay.Fukuandan) {
      payModalRef.current.openModal({
        payType: selectedPayType,
        showFukuanDanConfirm: true,
      });
      return;
    }
    const { name, certType, certNo, mobile, email, wyuid } = data;
    const { label, value } = selectedSalesperson || {};
    let params = {
      fkdFlag: selectedPayType === PayWay.Fukuandan,
      appChannel: 'PMS',
      name,
      certType,
      certNo,
      mobile,
      email,
      wyuid,
      price: memberCardPrice,
      saleEmployeeName: label,
      saleEmployeeNo: value,
      cardCode: selectedCardCode,
      cardName: memberCardList?.find((item) => item.cardCode === selectedCardCode)?.name,
      source: cardCoverImgMap[selectedCardCode]?.source,
    };
    if (memberInfo?.isNew) {
      // 身份证读卡器信息合并
      params = { ...params, ...filterFormParams(transformCardInfo(cardInfo)) };
    }
    buyMemberCardFn(params)
      .then((res) => {
        payModalRef.current.openModal({
          payAmount: memberCardPrice,
          payType: selectedPayType,
          payOrderType: 103,
          paymentNo: res,
          businessType: 2,
        });
      })
      .catch((error) => {
        // 请求头被拦截需额外抛出
        if (error?.code === 101920012 || error?.code === 102020011) {
          message.error(error?.msg);
        }
      });
  };

  // 收款
  const handleReceiveMoneyDebounce = debounce(async (confirm?: boolean) => {
    // 获取打开弹窗时身份证号错误
    if (idValidateInfo?.code === 101920012 || idValidateInfo?.code === 102020011) {
      message.error(idValidateInfo?.msg);
      return;
    }
    if (memberInfo?.isNew) {
      // 读卡
      formRef.current?.validateFields().then((values) => {
        const { wyuid } = memberInfo;
        const { name, mobile, email } = values;
        if (wyuid && (mobile || email)) {
          phoneCheckModalRef.current
            .showPhoneCheck([{ wyuid, name, mobile, email }])
            .then((res) => {
              if (res.success) {
                openPayModal({ ...memberInfo, ...values }, confirm);
              }
            });
        } else {
          // console.log('wyuid获取失败，请重试---读卡');
          // 可能获取网鱼uid接口挂掉或者根本没有网鱼uid
          openPayModal({ ...memberInfo, ...values }, confirm);
        }
      });
    } else {
      // 手机号可编辑，证件号不可编辑且没有onValuesChange，但仍然需要验证证件号
      detailRef.current?.validateFields().then(async (values) => {
        const { name, certType, certNo, wyuid } = memberInfo || {};
        if (!name) {
          message.error(intl.get('cpm_card_purchase_no_name'));
          return;
        }
        if (!certType || !certNo) {
          message.error(intl.get('cpm_card_purchase_no_cert_type_cert_no'));
          return;
        }
        if (!wyuid) {
          message.error(intl.get('cpm_card_purchase_no_wyuid'));
          return;
        }
        const { mobile, email } = values;
        if (wyuid && (mobile || email)) {
          phoneCheckModalRef.current
            .showPhoneCheck([{ wyuid, name, mobile, email }])
            .then((res) => {
              if (res.success) {
                openPayModal({ ...memberInfo, ...values }, confirm);
              }
            });
        } else {
          // console.log('wyuid获取失败，请重试---只有手机号可编辑');
          openPayModal({ ...memberInfo, ...values }, confirm);
        }
      });
    }
  }, 500);

  const handleCancel = () => {
    drawerRef.current.closeDrawer();
    emptyIdCardState();
    emptyMemberState();

    if (onCloseDrawer) {
      const formVal = memberInfo?.isNew
        ? formRef.current.getFieldsValue()
        : { ...memberInfo, ...detailRef.current.getFieldsValue() };
      onCloseDrawer({ isBuySuccess: false, ...formVal });
    }
  };

  const handleValuesChangeDebounce = debounce(async (changedValues) => {
    // 读取或输入证件号，loading消失
    if (changedValues.certType || changedValues.certNo) {
      emptyIdCardState();
    }
  }, 1000);

  // 当前姓名、证件类型、证件号输入离焦时获取网鱼uid
  const handleBlur = useCallback(() => {
    const values = formRef.current?.getFieldsValue();
    getMobileByCertNo({ ...memberInfo, ...values });
  }, [memberInfo]);

  const handleCardClick = (val) => setSelectedCardCode(val);

  const handleChangeSalesperson = (value) => {
    setSelectedSalesperson(value);
  };

  const handleSelectPayType = (payType) => {
    setSelectedPayType(payType);
  };

  const payResultCallback = ({ generatePayOrder, payed, result }) => {
    if (generatePayOrder) {
      drawerRef.current.closeDrawer();
      return;
    }
    const formVal = memberInfo?.isNew
      ? formRef.current.getFieldsValue()
      : { ...memberInfo, ...detailRef.current.getFieldsValue() };

    if (payed && result) {
      message.success(intl.get('cpm_card_purchase_vip_card_buy_success'));
      drawerRef.current.closeDrawer();
      emptyMemberState();
      if (onCloseDrawer) {
        onCloseDrawer({ isBuySuccess: true, ...formVal });
      }
    }
    if (payed && !result) {
      if (onCloseDrawer) {
        onCloseDrawer({ isBuySuccess: false, ...formVal });
      }
    }
  };

  useEffect(() => {
    return () => {
      emptyIdCardState();
      emptyMemberState();
    };
  }, []);

  useEffect(() => {
    if (Array.isArray(memberCardList) && memberCardList.length === 1) {
      setSelectedCardCode(memberCardList[0]?.cardCode);
    }
  }, [memberCardList]);

  useEffect(() => {
    // 默认勾选当前登录人为售卖人，如不在组织架构则选择其他
    if (Array.isArray(salespersonList)) {
      const { employeeNo } = userInfo || {};
      const index = salespersonList.findIndex((item) => item?.saleEmployeeNo === employeeNo);
      setSelectedSalesperson(
        index === -1
          ? { label: intl.get('app_other'), value: 'OTHERS' }
          : {
              label: salespersonList[index]?.saleEmployeeName,
              value: salespersonList[index]?.saleEmployeeNo,
            },
      );
    }
  }, [userInfo, salespersonList]);

  useImperativeHandle(
    myRef,
    () => ({
      ...drawerRef.current,
      openDrawer: handleOpenDrawer,
    }),
    [],
  );

  const formAttr: Array<IFieldItem> = useMemo(() => {
    return [
      {
        label: intl.get('app_name'),
        key: 'name',
        rules: [{ required: true, message: intl.get('cpm_card_purchase_words_limit_40'), max: 40 }],
        componentProps: {
          onBlur: handleBlur,
        },
      },
      {
        label: intl.get('app_reservation_cert_type'),
        key: 'certType',
        type: 'select',
        rules: [{ required: true, message: intl.get('cpm_card_purchase_select_cert_type') }],
        dataSource: TenantGlobalConfig.getSystemConfig().certTypesDataSource,
        componentProps: {
          onBlur: handleBlur,
        },
      },
      {
        label: intl.get('app_reservation_cert_number'),
        key: 'certNo',
        rules: [
          {
            required: true,
            message: intl.get('cpm_card_purchase_words_limit_30'),
            max: 30,
          },
        ],
        componentProps: {
          onBlur: handleBlur,
        },
      },
      {
        label: intl.get('bill_refound_res_mobile_no'),
        key: 'mobile',
        type: 'group',
        rules: [
          {
            validator(_, val) {
              const { areaCode, mobileStr } = parseAreaMobileStr(val);
              // 只填一个
              if ((areaCode && !mobileStr) || (!areaCode && mobileStr)) {
                return Promise.reject(
                  new Error(renderIntlLabel('reservation_error_mobile_invalid')),
                );
              }
              // 是否修改过，修改过校验是否脱敏
              if (val && val !== memberInfo?.mobile && val.includes('*')) {
                return Promise.reject(
                  new Error(renderIntlLabel('reservation_error_mobile_invalid')),
                );
              }
              // 关键人常用联系方式手机号是否必填
              if (
                TenantGlobalConfig.getSystemConfig().contractType === ContractType.MOBILE &&
                !val
              ) {
                return Promise.reject(
                  new Error(renderIntlLabel('reservation_error_mobile_invalid')),
                );
              }

              return Promise.resolve();
            },
          },
        ],
        component: <BaseMobileInput />,
      },
      {
        label: intl.get('help_store_address_type_email'),
        key: 'email',
        type: 'group',
        rules: [
          {
            validator(_, val) {
              // 是否修改过，修改过校验是否脱敏
              if (val && val !== memberInfo?.email && !EMAIL_REG.test(val)) {
                return Promise.reject(
                  new Error(intl.get('order_frontDesk_validate_please_check_email_error_format')),
                );
              }
              if (
                TenantGlobalConfig.getSystemConfig().contractType === ContractType.EMAIL &&
                !val
              ) {
                return Promise.reject(
                  new Error(
                    renderIntlLabel('order_frontDesk_validate_please_check_email_error_format'),
                  ),
                );
              }
              return Promise.resolve();
            },
          },
        ],
        component: <BaseEmailInput />,
      },
    ];
  }, [handleBlur, memberInfo]);

  const renderCardList = () => {
    if (!memberInfo?.certNo || !memberInfo.certType) {
      return <div>{intl.get('cpm_card_purchase_fill_vip_info_first')}</div>;
    }
    if (Array.isArray(memberCardList) && memberCardList?.length) {
      // console.log(memberCardList, 'memberCardList');
      return memberCardList?.map((item) => (
        <div
          key={item.cardCode}
          className={`${styles.item} ${item.cardCode === selectedCardCode ? styles.selected : ''}`}
          onClick={() => handleCardClick(item.cardCode)}
        >
          <img
            className={styles.img}
            src={
              item.cardCode === selectedCardCode
                ? cardCoverImgMap[item.cardCode]?.selectedUrl
                : cardCoverImgMap[item.cardCode]?.url
            }
            style={{
              width: item.cardCode === 'K0005' ? 120 : 114,
              height: item.cardCode === 'K0005' ? 80 : 72,
              marginTop: item.cardCode === 'K0005' ? -8 : 0,
            }}
            alt={item.name}
          />

          <span
            className={cls(
              styles.memberCardName,
              cardCoverImgMap[item.cardCode]?.bgColorStyle || '',
            )}
          >
            {cardCoverImgMap[item.cardCode]?.copywriting}
          </span>
        </div>
      ));
    }
    return <div>{intl.get('cpm_card_purchase_no_vip_card_can_buy')}</div>;
  };

  return (
    <>
      <CustomDrawer
        width="90%"
        title={intl.get('cpm_card_purchase_buy_vip_card')}
        footer={
          <>
            <Button
              type="primary"
              loading={buyCardLoading}
              onClick={() => handleReceiveMoneyDebounce()}
            >
              {intl.get('bill_credit_collection')}
            </Button>
            <Button onClick={handleCancel}>{intl.get('app_confirm_cancel_btn')}</Button>
          </>
        }
        onClose={handleCancel}
        ref={drawerRef}
      >
        <PhoneCheckModal innerRef={phoneCheckModalRef} />

        <div className={styles.container}>
          {memberInfo?.isNew && showCertLoading ? (
            <Alert message={intl.get('cpm_card_purchase_reading_idcard')} type="info" showIcon />
          ) : null}
          {memberInfo?.isNew && !showCertLoading && isReadCertSuccess ? (
            <Alert message={intl.get('cpm_card_purchase_read_idcard')} type="success" showIcon />
          ) : null}
          <div className="space-between" style={{ margin: '24px 0' }}>
            <div className="groupLabel">{intl.get('cpm_card_purchase_vip_info')}</div>
          </div>
          {memberInfo?.isNew ? (
            <CustomForm
              ref={formRef}
              labelCol={{ span: 2 }}
              labelAlign="left"
              wrapperCol={{ span: 8 }}
              formAttr={formAttr}
              value={memberInfo}
              onValuesChange={handleValuesChangeDebounce}
            />
          ) : (
            <>
              {/* <Descriptions title="" column={1}>
                <Descriptions.Item label={intl.get('app_name')}>
                  {memberInfo.name}
                </Descriptions.Item>
                <Descriptions.Item label={intl.get('app_reservation_cert_type')}>
                  {TenantGlobalConfig.getSystemConfig().certTypesMap?.[memberInfo.certType]}
                </Descriptions.Item>
                <Descriptions.Item label={intl.get('app_reservation_cert_number')}>
                  {memberInfo.certNo}
                </Descriptions.Item>
              </Descriptions> */}
              <Form.Item labelCol={{ span: 2 }} labelAlign="left" label={intl.get('app_name')}>
                {memberInfo.name}
              </Form.Item>
              <Form.Item
                labelCol={{ span: 2 }}
                labelAlign="left"
                label={intl.get('app_reservation_cert_type')}
              >
                {TenantGlobalConfig.getSystemConfig().certTypesMap?.[memberInfo.certType]}
              </Form.Item>
              <Form.Item
                labelCol={{ span: 2 }}
                labelAlign="left"
                label={intl.get('app_reservation_cert_number')}
              >
                {memberInfo.certNo}
              </Form.Item>
              <CustomForm
                ref={detailRef}
                labelCol={{ span: 2 }}
                labelAlign="left"
                wrapperCol={{ span: 8 }}
                formAttr={detailAttr}
                value={memberInfo}
              />
            </>
          )}

          <div className="space-between" style={{ margin: '16px 0 24px' }}>
            <div className="groupLabel">{intl.get('cpm_card_purchase_sale_info')}</div>
          </div>
          <div style={{ overflow: 'hidden' }}>
            <Form.Item
              label={intl.get('bill_invoice_modal_vip_card')}
              labelCol={{ span: 2 }}
              labelAlign="left"
              wrapperCol={{ span: 22 }}
            >
              <div className={styles.cardContainer}>{renderCardList()}</div>
            </Form.Item>
            <Form.Item
              label={intl.get('cpm_card_purchase_saler')}
              labelCol={{ span: 2 }}
              labelAlign="left"
              wrapperCol={{ span: 8 }}
            >
              <Select
                options={salespersonList?.map((item) => ({
                  label: item.saleEmployeeName,
                  value: item.saleEmployeeNo,
                }))}
                labelInValue
                value={selectedSalesperson}
                onChange={handleChangeSalesperson}
              />
            </Form.Item>
          </div>

          <div className="space-between" style={{ margin: '16px 0 24px' }}>
            <div className="groupLabel">{intl.get('bill_credit_cashier')}</div>
          </div>
          <div style={{ marginBottom: '24px' }}>
            <span className="text-colon">{intl.get('bill_credit_account_receivable')}</span>
            <span className="amount-pay">
              {!isNil(memberCardPrice) ? parseFen2Yuan(memberCardPrice) : '—'}
            </span>
          </div>
          <Payment isInitPayTypes businessType={2} selectPayType={handleSelectPayType} />
          <PayModal
            ref={payModalRef}
            payResultCallback={payResultCallback}
            onConfirmOk={() => handleReceiveMoneyDebounce(true)}
          />
        </div>
      </CustomDrawer>
    </>
  );
};

const mapStateToProps = (state) => {
  return { ...state.guest, ...state.user };
};

const ConnectComponent: React.FC<IcardPurchaseProps> = connect(mapStateToProps, {
  querySalespersonFn: querySalesperson,
  buyMemberCardFn: buyMemberCard,
  updateIdValidatedResFn: updateIdValidatedRes,
})(React.memo(CardPurchase));

export default React.forwardRef<IcardPurchaseRef, IcardPurchaseProps>((props, ref) => (
  <ConnectComponent {...props} myRef={ref} />
));
